import {createApiCall, createFileUploadApiCall, fetchAllPages} from '../utils';
import {SERVICE_NAMES} from '../constants';
import type {ApiCallOptions, PaginatedResponse} from '../types';
import type {
    ApiResponseWrapperAttachmentUploadView,
    AttachmentUploadListParams,
    AttachmentUploadView
} from './api.d';

export const rejectAttachmentUpload = (transactionId: string, attachmentId: string, attachmentUploadId: string, data: unknown, options: ApiCallOptions = {}) => createApiCall<ApiResponseWrapperAttachmentUploadView>(SERVICE_NAMES.ATTACHMENT_UPLOAD, 'PUT', `transactions/${transactionId}/attachments/${attachmentId}/attachment-uploads/${attachmentUploadId}/reject`)({...options, data});

export const acceptAttachmentUpload = (transactionId: string, attachmentId: string, attachmentUploadId: string, options: ApiCallOptions = {}) => createApiCall<ApiResponseWrapperAttachmentUploadView>(SERVICE_NAMES.ATTACHMENT_UPLOAD, 'PUT', `transactions/${transactionId}/attachments/${attachmentId}/attachment-uploads/${attachmentUploadId}/accept`)({...options});

export const getAttachmentUploads = (transactionId: string, attachmentId: string, params?: AttachmentUploadListParams, options: ApiCallOptions = {}) => {
    const queryParams = new URLSearchParams();

    if (params?.per_page !== undefined) queryParams.append('per_page', params.per_page.toString());
    if (params?.page !== undefined) queryParams.append('page', params.page.toString());

    const queryString = queryParams.toString();
    const endpoint = queryString
        ? `transactions/${transactionId}/attachments/${attachmentId}/attachment-uploads?${queryString}`
        : `transactions/${transactionId}/attachments/${attachmentId}/attachment-uploads`;

    return createApiCall<PaginatedResponse<AttachmentUploadView>>(SERVICE_NAMES.ATTACHMENT_UPLOAD, 'GET', endpoint)({...options});
};

export const getAllAttachmentUploads = (transactionId: string, attachmentId: string, params?: Omit<AttachmentUploadListParams, 'page'>, options?: ApiCallOptions) => fetchAllPages(
    (paginationParams: AttachmentUploadListParams, opts?: ApiCallOptions) => getAttachmentUploads(transactionId, attachmentId, paginationParams, opts),
    params || {},
    options
) as Promise<AttachmentUploadView[]>;

export const uploadAttachment = (transactionId: string, attachmentId: string, file: File | Blob, options: ApiCallOptions = {}) => createFileUploadApiCall<ApiResponseWrapperAttachmentUploadView>(SERVICE_NAMES.ATTACHMENT_UPLOAD, `transactions/${transactionId}/attachments/${attachmentId}/attachment-uploads`)(file, {
    ...options
});

export const getAttachmentUpload = (transactionId: string, attachmentId: string, attachmentUploadId: string, options: ApiCallOptions = {}) => createApiCall<ApiResponseWrapperAttachmentUploadView>(SERVICE_NAMES.ATTACHMENT_UPLOAD, 'GET', `transactions/${transactionId}/attachments/${attachmentId}/attachment-uploads/${attachmentUploadId}`)({...options});

export const deleteAttachmentUpload = (transactionId: string, attachmentId: string, attachmentUploadId: string, options: ApiCallOptions = {}) => createApiCall<void>(SERVICE_NAMES.ATTACHMENT_UPLOAD, 'DELETE', `transactions/${transactionId}/attachments/${attachmentId}/attachment-uploads/${attachmentUploadId}`)({...options});

export const downloadAttachment = (transactionId: string, attachmentId: string, attachmentUploadId: string, options: ApiCallOptions = {}) => createApiCall<Blob>(SERVICE_NAMES.ATTACHMENT_UPLOAD, 'GET', `transactions/${transactionId}/attachments/${attachmentId}/attachment-uploads/${attachmentUploadId}/file`)({
    ...options,
    responseType: 'blob',
    headers: {
        ...options.headers,
        Accept: '*/*'
    }
});

export const downloadAttachmentUploadsAsZip = (transactionId: string, attachmentId: string, options: ApiCallOptions = {}) => createApiCall<Blob>(SERVICE_NAMES.ATTACHMENT_UPLOAD, 'GET', `transactions/${transactionId}/attachments/${attachmentId}/zip`)({
    ...options,
    responseType: 'blob'
});

export const downloadAllAttachmentsAsZip = (transactionId: string, options: ApiCallOptions = {}) => createApiCall<Blob>(SERVICE_NAMES.ATTACHMENT_UPLOAD, 'GET', `transactions/${transactionId}/attachments/zip`)({
    ...options,
    responseType: 'blob'
});

export const downloadAttachmentsForRecipientAsZip = (transactionId: string, recipientId: string, options: ApiCallOptions = {}) => createApiCall<Blob>(SERVICE_NAMES.ATTACHMENT_UPLOAD, 'GET', `transactions/${transactionId}/attachments/recipients/${recipientId}/zip`)({
    ...options,
    responseType: 'blob'
});
